import React from "react";
import GuidebookViewer, {
  GuidebookStyleFormData,
  GuidebookViewerSection,
} from "../../components/GuidebookViewer";
import { LoaderComponent } from "../../components";
import { API_URL } from "../../config/endpoints";
import { useGuidebook } from "../../hooks/useGuidebook";
import { useGuidebookShare } from "../../hooks/useGuidebookShare";
import { useWindowSize } from "../../hooks/useWindowSize";
import {
  GuidebookSection,
  GuidebookSectionLink,
  GuidebookSectionMedia,
} from "../../types/guidebook";
import {
  DesktopFrame,
  MobileFrame,
  Page,
  ScrollContainer,
  StateCard,
} from "./styles";
import { useParams } from "react-router-dom";

const DEFAULT_STYLES: Required<GuidebookStyleFormData> = {
  backgroundColor: "#F8FAFC",
  fontColor: "#6D28D9",
  fontFamily: "Poppins, sans-serif",
  headingFontSize: "24px",
  headingFontWeight: "600",
  textFontSize: "16px",
  textFontWeight: "400",
};

const getApiOrigin = (): string => {
  try {
    return new URL((API_URL || "").trim()).origin;
  } catch (e) {
    return "";
  }
};

const toAssetUrl = (url?: string | null): string => {
  const value = (url || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("//")) return `https:${value}`;
  if (!value.startsWith("/")) return value;

  const apiOrigin = getApiOrigin();
  return apiOrigin ? `${apiOrigin}${value}` : value;
};

const buildUploadedMediaUrl = (url?: string | null): string => {
  const value = (url || "").trim();
  if (!value) return "";

  const apiOrigin = getApiOrigin();

  try {
    const parsed = /^https?:\/\//i.test(value) ? new URL(value) : null;
    const origin = parsed?.origin || apiOrigin;
    const pathname = parsed?.pathname || value;
    const search = parsed?.search || "";

    if (
      pathname.includes("/media/upload/") ||
      pathname.includes("/media/cache/")
    ) {
      return `${origin}${pathname}${search}`;
    }

    let mediaTail = "";
    if (pathname.includes("/api/media/")) {
      mediaTail = pathname.split("/api/media/")[1] || "";
    } else if (pathname.includes("/media/")) {
      mediaTail = pathname.split("/media/")[1] || "";
    }

    if (!mediaTail) {
      return toAssetUrl(value);
    }

    return `${origin}/media/upload/${mediaTail}${search}`;
  } catch (e) {
    return toAssetUrl(value);
  }
};

const buildCachedImageUrl = (url?: string | null): string => {
  const value = (url || "").trim();
  if (!value) return "";

  const apiOrigin = getApiOrigin();

  try {
    const parsed = /^https?:\/\//i.test(value) ? new URL(value) : null;
    const origin = parsed?.origin || apiOrigin;
    const pathname = parsed?.pathname || value;
    const search = parsed?.search || "";

    if (pathname.includes("/media/cache/")) {
      return `${origin}${pathname}${search}`;
    }

    let mediaTail = "";
    if (pathname.includes("/api/media/")) {
      mediaTail = pathname.split("/api/media/")[1] || "";
    } else if (pathname.includes("/media/")) {
      mediaTail = pathname.split("/media/")[1] || "";
    }

    if (!mediaTail) {
      return toAssetUrl(value);
    }

    const uploadTail = mediaTail.startsWith("upload/")
      ? mediaTail
      : `upload/${mediaTail}`;

    return `${origin}/media/cache/medium/media/${uploadTail}${search}`;
  } catch (e) {
    return toAssetUrl(value);
  }
};

const getImageUrls = (media: GuidebookSectionMedia["media"]): string[] => {
  return Array.from(
    new Set(
      [
        toAssetUrl(media.urls?.medium || ""),
        toAssetUrl(media.urls?.small || ""),
        toAssetUrl(media.urls?.big || ""),
        toAssetUrl(media.urls?.square || ""),
        buildCachedImageUrl(media.url || ""),
        buildUploadedMediaUrl(media.url || ""),
        toAssetUrl(media.url || ""),
      ].filter(Boolean)
    )
  );
};

const getPdfUrl = (url?: string | null): string => {
  return buildUploadedMediaUrl(url || "") || toAssetUrl(url || "");
};

const getReadableFileName = (
  url?: string | null,
  originalName?: string | null,
  fallback?: string | null
): string => {
  const explicitName = (originalName || "").trim();
  if (explicitName) return explicitName;

  const fallbackName = (fallback || "").trim();
  if (fallbackName) return fallbackName;

  const value = (url || "").trim();
  if (!value) return "Download PDF";

  try {
    const pathname = /^https?:\/\//i.test(value)
      ? new URL(value).pathname
      : value;
    const fileName = decodeURIComponent(pathname.split("/").pop() || "").trim();
    if (!fileName) return "Download PDF";

    const extensionIndex = fileName.lastIndexOf(".");
    const extension =
      extensionIndex >= 0 ? fileName.slice(extensionIndex + 1).toLowerCase() : "";
    const baseName =
      extensionIndex >= 0 ? fileName.slice(0, extensionIndex) : fileName;

    const normalizedBaseName = baseName
      .replace(/-[a-f0-9]{12,}$/i, "")
      .replace(/[-_]+/g, " ")
      .trim();

    if (!normalizedBaseName) return fileName;

    const readableBaseName =
      normalizedBaseName.charAt(0).toUpperCase() + normalizedBaseName.slice(1);

    return extension ? `${readableBaseName}.${extension}` : readableBaseName;
  } catch (e) {
    return "Download PDF";
  }
};

const toEmbedYouTube = (url: string): string => {
  if (url.includes("youtube.com/embed/")) return url;
  if (url.includes("youtube.com/watch?v=")) {
    const videoId = url.split("v=")[1]?.split("&")[0] || "";
    return `https://www.youtube.com/embed/${videoId}`;
  }
  if (url.includes("youtu.be/")) {
    const videoId = url.split("youtu.be/")[1]?.split("?")[0] || "";
    return `https://www.youtube.com/embed/${videoId}`;
  }
  return url;
};

const resolveIconType = (section: GuidebookSection): string | undefined => {
  return section.iconType || section.iconName || section.type;
};

const normalizeSectionContent = (content?: string): string[] => {
  if (!content) return [];
  if (/<[^>]+>/.test(content)) return [content];
  return content.split("\n").filter((line) => line.trim() !== "");
};

const GuidebookPreview: React.FC = () => {
  const { guidebookUid, unitUid, shortCode } = useParams<{
    guidebookUid?: string;
    unitUid?: string;
    shortCode?: string;
  }>();
  const { isXs, isSm } = useWindowSize();
  const isMobile = isXs || isSm;
  const previewScrollRef = React.useRef<HTMLDivElement>(null);
  const isShortCodeRoute = Boolean(shortCode);

  const {
    guidebook: routeGuidebook,
    loading: routeLoading,
    error: routeError,
  } = useGuidebook(
    guidebookUid || "",
    unitUid || ""
  );
  const {
    guidebook: shareGuidebook,
    loading: shareLoading,
    error: shareError,
  } = useGuidebookShare(shortCode || "");

  const guidebook = isShortCodeRoute ? shareGuidebook : routeGuidebook;
  const loading = isShortCodeRoute ? shareLoading : routeLoading;
  const error = isShortCodeRoute ? shareError : routeError;

  const sections = React.useMemo<GuidebookViewerSection[]>(() => {
    const sorted = [...(guidebook?.sections || [])].sort((a, b) => a.order - b.order);

    return sorted.map((section) => {
      const imageMediaFromSectionMedia = (section.sectionMedia || [])
        .filter((media) => media?.media?.mimeType?.startsWith("image/"))
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((media) => {
          const [src, ...fallbackSrcs] = getImageUrls(media.media);
          if (!src) return null;
          return { src, fallbackSrcs };
        })
        .filter(Boolean) as Array<{ src: string; fallbackSrcs?: string[] }>;

      const imageMediaFromLegacy = (section.media || [])
        .filter((media) => media?.mimeType?.startsWith("image/"))
        .map((media) => {
          const urls = [
            toAssetUrl(media.urls?.medium || ""),
            toAssetUrl(media.urls?.small || ""),
            toAssetUrl(media.url || ""),
          ].filter(Boolean);
          const [src, ...fallbackSrcs] = urls;
          if (!src) return null;
          return { src, fallbackSrcs };
        })
        .filter(Boolean) as Array<{ src: string; fallbackSrcs?: string[] }>;

      const imageMedia =
        imageMediaFromSectionMedia.length > 0
          ? imageMediaFromSectionMedia
          : imageMediaFromLegacy;

      const uploadedVideos = (section.sectionMedia || [])
        .filter((media) => media?.media?.mimeType?.startsWith("video/"))
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((media) => buildUploadedMediaUrl(media.media.url || ""))
        .filter(Boolean) as string[];

      const externalVideos = (section.externalVideoUrls || []).map(toEmbedYouTube);

      const links = [...(section.links || [])]
        .filter(
          (link): link is GuidebookSectionLink =>
            typeof link !== "string" && Boolean(link?.url)
        )
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((link) => ({ title: link.title, url: link.url }));

      const pdfs = (section.sectionMedia || [])
        .filter((media) => media?.media?.mimeType === "application/pdf")
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((media) => ({
          url: getPdfUrl(media.media.url || ""),
          originalName: getReadableFileName(
            media.media.url,
            media.media.originalName,
            media.caption
          ),
        }))
        .filter((pdf) => pdf.url);

      return {
        id: section.uid,
        title: section.title,
        iconType: resolveIconType(section),
        content: normalizeSectionContent(section.content),
        collapseMode: section.customStyles?.collapseMode ?? "automatic",
        images: imageMedia,
        externalVideoUrls: [...externalVideos, ...uploadedVideos],
        links,
        pdfs,
        styleOverrides: {
          backgroundColor: section.customStyles?.backgroundColor,
          fontColor:
            section.customStyles?.fontColor || section.customStyles?.textColor,
          fontFamily: section.customStyles?.fontFamily,
          headingFontSize: section.customStyles?.headingFontSize,
          headingFontWeight: section.customStyles?.headingFontWeight,
          textFontSize:
            section.customStyles?.textFontSize || section.customStyles?.fontSize,
          textFontWeight:
            section.customStyles?.textFontWeight || section.customStyles?.fontWeight,
        },
      };
    });
  }, [guidebook?.sections]);

  const globalStyles = React.useMemo(() => {
    const style = guidebook?.globalStyle || guidebook?.globalStyles;

    return {
      backgroundColor:
        style?.backgroundColor || style?.primaryColor || DEFAULT_STYLES.backgroundColor,
      fontColor:
        style?.fontColor ||
        style?.textColor ||
        style?.secondaryColor ||
        DEFAULT_STYLES.fontColor,
      fontFamily: style?.fontFamily || DEFAULT_STYLES.fontFamily,
      headingFontSize: style?.headingFontSize || DEFAULT_STYLES.headingFontSize,
      headingFontWeight:
        style?.headingFontWeight || DEFAULT_STYLES.headingFontWeight,
      textFontSize:
        style?.textFontSize || style?.fontSize || DEFAULT_STYLES.textFontSize,
      textFontWeight:
        style?.textFontWeight || style?.fontWeight || DEFAULT_STYLES.textFontWeight,
    };
  }, [guidebook?.globalStyle, guidebook?.globalStyles]);

  if (loading) {
    return <LoaderComponent content="Loading guidebook..." />;
  }

  if (!isShortCodeRoute && !guidebookUid) {
    return (
      <Page>
        <StateCard>
          <h2>Missing guidebook UUID</h2>
          <p>The URL must include the guidebook UUID.</p>
        </StateCard>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <StateCard>
          <h2>Failed to load guidebook</h2>
          <p>{error}</p>
        </StateCard>
      </Page>
    );
  }

  if (!guidebook) {
    return (
      <Page>
        <StateCard>
          <h2>Guidebook not found</h2>
          <p>The requested guidebook could not be found.</p>
        </StateCard>
      </Page>
    );
  }

  const content = (
    <ScrollContainer ref={previewScrollRef}>
      <GuidebookViewer
        title={guidebook.title}
        sections={sections}
        globalStyles={globalStyles}
        shortcodeValues={{}}
        contextLabel={guidebook?.propertyName || null}
        scrollContainerRef={previewScrollRef}
        forceMobileNav={isMobile}
      />
    </ScrollContainer>
  );

  return (
    <Page>
      {isMobile ? (
        <MobileFrame $backgroundColor={globalStyles.backgroundColor}>
          {content}
        </MobileFrame>
      ) : (
        <DesktopFrame $backgroundColor={globalStyles.backgroundColor}>
          {content}
        </DesktopFrame>
      )}
    </Page>
  );
};

export default GuidebookPreview;
